// 안전 산술식 평가기 — eval/Function/new Function 절대 사용 안 함.
//
// 계산 셀(CellCompute)의 expr 모드에서 운영자가 입력한 산술식을 평가한다. 임의 코드 실행을
// 막기 위해 순수 토크나이저 + 재귀하강 파서로 직접 평가한다. 허용 토큰은 숫자/식별자/사칙연산자/
// 괄호뿐이며, 함수 호출·속성 접근·대괄호·쉼표 등은 토큰화 단계에서 전부 거부(throw)한다.
//
// 문법(우선순위 낮→높):
//   expr   := term (('+' | '-') term)*
//   term   := unary (('*' | '/') unary)*
//   unary  := ('-' | '+') unary | primary
//   primary:= number | identifier | '(' expr ')'
//
// 식별자는 vars에서 조회하며 없으면 0. 0 나눗셈은 JS 기본 동작(±Infinity)을 그대로 반환한다
// (호출부 computeCellCompute가 비유한수를 빈값으로 처리).

type Token =
  | { type: 'number'; value: number }
  | { type: 'ident'; value: string }
  | { type: 'op'; value: '+' | '-' | '*' | '/' }
  | { type: 'lparen' }
  | { type: 'rparen' };

/** 입력 문자열을 토큰 배열로 분해한다. 허용 외 문자를 만나면 throw. */
function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = input.length;

  while (i < n) {
    const ch = input[i];

    // 공백 skip
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i += 1;
      continue;
    }

    // 숫자(정수/소수) — 1, 1.5, .5
    if ((ch >= '0' && ch <= '9') || ch === '.') {
      let j = i;
      let seenDot = false;
      while (j < n) {
        const c = input[j];
        if (c >= '0' && c <= '9') {
          j += 1;
        } else if (c === '.' && !seenDot) {
          seenDot = true;
          j += 1;
        } else {
          break;
        }
      }
      const slice = input.slice(i, j);
      const value = Number(slice);
      if (!Number.isFinite(value)) {
        throw new Error(`잘못된 숫자: ${slice}`);
      }
      tokens.push({ type: 'number', value });
      i = j;
      continue;
    }

    // 식별자 [A-Za-z_][A-Za-z0-9_]*
    if ((ch >= 'A' && ch <= 'Z') || (ch >= 'a' && ch <= 'z') || ch === '_') {
      let j = i + 1;
      while (j < n) {
        const c = input[j];
        if (
          (c >= 'A' && c <= 'Z') ||
          (c >= 'a' && c <= 'z') ||
          (c >= '0' && c <= '9') ||
          c === '_'
        ) {
          j += 1;
        } else {
          break;
        }
      }
      tokens.push({ type: 'ident', value: input.slice(i, j) });
      i = j;
      continue;
    }

    // 연산자/괄호
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/') {
      tokens.push({ type: 'op', value: ch });
      i += 1;
      continue;
    }
    if (ch === '(') {
      tokens.push({ type: 'lparen' });
      i += 1;
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen' });
      i += 1;
      continue;
    }

    // 그 외 문자(함수 호출 '()' 인자, '.', '[', ']', ',', ';', 등)는 전부 거부
    throw new Error(`허용되지 않은 문자: '${ch}'`);
  }

  return tokens;
}

/** 재귀하강 파서 + 즉시 평가기. */
class Parser {
  private pos = 0;

  constructor(
    private readonly tokens: Token[],
    private readonly vars: Record<string, number>,
  ) {}

  parse(): number {
    if (this.tokens.length === 0) {
      throw new Error('빈 수식');
    }
    const result = this.parseExpr();
    if (this.pos !== this.tokens.length) {
      throw new Error('수식 파싱 실패: 남은 토큰');
    }
    return result;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  // expr := term (('+' | '-') term)*
  private parseExpr(): number {
    let value = this.parseTerm();
    let tok = this.peek();
    while (tok && tok.type === 'op' && (tok.value === '+' || tok.value === '-')) {
      this.pos += 1;
      const rhs = this.parseTerm();
      value = tok.value === '+' ? value + rhs : value - rhs;
      tok = this.peek();
    }
    return value;
  }

  // term := unary (('*' | '/') unary)*
  private parseTerm(): number {
    let value = this.parseUnary();
    let tok = this.peek();
    while (tok && tok.type === 'op' && (tok.value === '*' || tok.value === '/')) {
      this.pos += 1;
      const rhs = this.parseUnary();
      value = tok.value === '*' ? value * rhs : value / rhs;
      tok = this.peek();
    }
    return value;
  }

  // unary := ('-' | '+') unary | primary
  private parseUnary(): number {
    const tok = this.peek();
    if (tok && tok.type === 'op' && (tok.value === '-' || tok.value === '+')) {
      this.pos += 1;
      const operand = this.parseUnary();
      return tok.value === '-' ? -operand : operand;
    }
    return this.parsePrimary();
  }

  // primary := number | identifier | '(' expr ')'
  private parsePrimary(): number {
    const tok = this.peek();
    if (!tok) {
      throw new Error('수식 파싱 실패: 피연산자 누락');
    }
    if (tok.type === 'number') {
      this.pos += 1;
      return tok.value;
    }
    if (tok.type === 'ident') {
      this.pos += 1;
      const v = this.vars[tok.value];
      return typeof v === 'number' && Number.isFinite(v) ? v : 0;
    }
    if (tok.type === 'lparen') {
      this.pos += 1;
      const inner = this.parseExpr();
      const close = this.peek();
      if (!close || close.type !== 'rparen') {
        throw new Error('수식 파싱 실패: 닫는 괄호 누락');
      }
      this.pos += 1;
      return inner;
    }
    throw new Error('수식 파싱 실패: 예상치 못한 토큰');
  }
}

/**
 * 안전 산술식을 평가한다. eval/Function 미사용 — 토크나이저+재귀하강 파서로 직접 계산.
 *
 * @param expr 사칙연산/괄호/단항마이너스만 허용하는 산술식
 * @param vars 식별자 → 수치 맵(없는 식별자는 0)
 * @returns 평가 결과(0 나눗셈 시 ±Infinity는 JS 기본 동작 그대로). 허용 외 토큰/파싱 실패 시 throw.
 */
export function evalSafeExpr(expr: string, vars: Record<string, number>): number {
  const tokens = tokenize(expr);
  return new Parser(tokens, vars).parse();
}
